import React, { useState, useEffect } from "react";
import {
  FileDownloadOutlined as FileDownloadOutlinedIcon,
  ChevronRight as ChevronRightIcon,
  InfoOutlined as InfoOutlinedIcon,
  Check as CheckIcon,
  EditOutlined as EditOutlinedIcon,
} from "@mui/icons-material";
import { IconButton } from "@mui/material";

import Daterange from "components/common/Daterange";
import TableComponent from "components/common/TableComponent";
import CustomChip from "components/common/Chip";
import DateRangeSelect from "components/common/DateRangeSelect";
import CustomButton from "components/common/Button";
import CustomTooltip from "components/common/Tooltip";
import TimesheetDetailModal from "components/Screens/Timesheet/TimesheetDetailModal";
import TimesheetDetailModalV2 from "components/Screens/Timesheet/TimesheetDetailModal/TimesheetDetailModalV2";
import EditShiftModal from "./EditShiftModal";
import PageLoader from "components/common/PageLoader";
import TopHeader from "components/common/TopHeader";

import { getLoggedInUserDetailsFromLocalStorage } from "helpers";
import { formatDateWithoutTimeZone } from "components/Screens/Timesheet/helpers";
import { getEmployeeTimesheet, exportEmployeeTimesheetCSV } from "services/CheckInCheckOut/TimesheetGetAndUpdate";
import { getAttendanceDetails, getEmployeeSchedule } from "services/CheckInCheckOut/CheckInAndBreaks";

import "./Timesheet.scss";
import { useFlags } from "launchdarkly-react-client-sdk";

const ROWS_PER_PAGE_OPTIONS = [10, 25, 50, 100];

/**
 * Format a stored timesheet datetime to a 12-hour "h:mm AM/PM" label.
 *
 * The stored timestamp is a naive wall-clock that happens to be tagged "Z"
 * (e.g. "2026-06-24T16:16:00.000Z"). We read its UTC components so the displayed
 * HH:mm always equals the stored HH:mm, independent of the viewer's browser
 * timezone (no timezone conversion is performed).
 *
 * @param {string} dateTimeString - ISO ("...T..Z"), "YYYY-MM-DD HH:mm:ss", or null/"-"
 * @returns {string} Time in "h:mm AM/PM" format, or "-" when empty/invalid
 */
export const formatTime = (dateTimeString) => {
  if (!dateTimeString || dateTimeString === "-") return "-";

  let hours;
  let minutes;
  if (dateTimeString.includes("T")) {
    const date = new Date(dateTimeString);
    if (Number.isNaN(date.getTime())) return "-";
    hours = date.getUTCHours();
    minutes = date.getUTCMinutes();
  } else if (dateTimeString.includes(" ")) {
    const [, timePart] = dateTimeString.split(" ");
    const [rawHours, rawMinutes] = timePart.split(":");
    hours = parseInt(rawHours, 10);
    minutes = parseInt(rawMinutes || 0, 10);
    if (Number.isNaN(hours) || Number.isNaN(minutes)) return "-";
  } else {
    const date = new Date(dateTimeString);
    if (Number.isNaN(date.getTime())) return "-";
    hours = date.getUTCHours();
    minutes = date.getUTCMinutes();
  }

  const ampm = hours >= 12 ? "PM" : "AM";
  const displayHour = hours % 12 || 12;
  const displayMinutes = minutes.toString().padStart(2, "0");

  return `${displayHour}:${displayMinutes} ${ampm}`;
};

function Timesheet({ employeeId, employeeDetails = {} }) {
  const { checkInRevamped, platformHeader } = useFlags();
  const loggedInUser = getLoggedInUserDetailsFromLocalStorage();

  const getDefaultDateRange = () => {
    const today = new Date();
    const pastDate = new Date();
    pastDate.setDate(today.getDate() - 30);

    return {
      startDate: pastDate,
      endDate: today,
    };
  };

  const [dateRange, setDateRange] = useState(getDefaultDateRange());
  const [detailModalOpen, setDetailModalOpen] = useState(false);
  const [selectedDetailRow, setSelectedDetailRow] = useState(null);
  const [loading, setLoading] = useState(true);
  const [timesheetData, setTimesheetData] = useState({ rows: [], stats: {} });
  const [attendanceDetails, setAttendanceDetails] = useState(null);
  const [schedule, setSchedule] = useState([]);
  const [showMissedShiftsOnly, setShowMissedShiftsOnly] = useState(false);
  const [editShiftModalOpen, setEditShiftModalOpen] = useState(false);
  const [timezone, setTimezone] = useState(null);

  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(10);
  const [totalCount, setTotalCount] = useState(0);

  const [tableResetKey, setTableResetKey] = useState(0);

  const userId = employeeId || loggedInUser?.userId;

  const convertSecondsToHoursMinutes = (seconds) => {
    if (!seconds || seconds === 0) return "0 hr 0 min";
    const hours = Math.floor(seconds / 3600);
    const minutes = Math.floor((seconds % 3600) / 60);
    return `${hours} hr ${minutes} min`;
  };

  const formatCurrentDaySchedule = () => {
    if (!schedule || !Array.isArray(schedule) || schedule.length === 0) {
      return "N/A";
    }

    const today = new Date().getDay();
    const daySchedule = schedule.find((scheduleItem) => scheduleItem.dayOfWeek === today);

    if (!daySchedule || !daySchedule.startTime || !daySchedule.endTime) {
      return "N/A";
    }

    const dayNames = ["Sun", "Mon", "Tue", "Wed", "Thu", "Fri", "Sat"];
    const dayName = dayNames[today];

    const formatTimeTo12Hour = (timeString) => {
      const [hours, minutes] = timeString.split(":");
      const hour = parseInt(hours, 10);
      const ampm = hour >= 12 ? "PM" : "AM";
      const displayHour = hour % 12 || 12;
      return `${displayHour}:${minutes} ${ampm}`;
    };

    const startTimeFormatted = formatTimeTo12Hour(daySchedule.startTime);
    const endTimeFormatted = formatTimeTo12Hour(daySchedule.endTime);

    return `${dayName} - ${startTimeFormatted} - ${endTimeFormatted}`;
  };

  const transformedTableData =
    timesheetData.rows?.map((row, index) => {
      // Check if clock-out is on the next day
      let hasNextDay = false;
      if (row.clockIn && row.clockOut) {
        // Extract just the date part from the datetime strings
        const clockInDay = row.clockIn.split(" ")[0];
        const clockOutDay = row.clockOut.split(" ")[0];

        hasNextDay = clockInDay !== clockOutDay;
      }

      const isWeekend = row.status === "Weekend";
      const isHoliday = row.status === "Holiday";
      const isMissedShift = row.status === "Missed shift";
      const shouldShowDash = isWeekend || isHoliday || isMissedShift;

      return {
        id: row.timesheetId || index,
        timesheetId: row.timesheetId,
        date: formatDateWithoutTimeZone(row.date),
        rawDate: row.date,
        status: row.status,
        clockedIn: formatTime(row.clockIn),
        clockedOut: formatTime(row.clockOut),
        timeWorked: shouldShowDash ? "-" : convertSecondsToHoursMinutes(row.timeWorkedSeconds),
        timeWorkedSeconds: row.timeWorkedSeconds,
        breakDuration: shouldShowDash ? "-" : convertSecondsToHoursMinutes(row.breakSeconds),
        breakSeconds: row.breakSeconds,
        nursing: shouldShowDash ? "-" : convertSecondsToHoursMinutes(row.nursingBreakSeconds),
        nursingBreakSeconds: row.nursingBreakSeconds,
        missing: [],
        hasNextDay,
        isMissedShift,
        isWeekend,
        isHoliday,
        hasMissingChips: false,
      };
    }) || [];

  const formatDateToYYYYMMDD = (date) => {
    if (!date) return "";
    const d = new Date(date);
    const year = d.getFullYear();
    const month = String(d.getMonth() + 1).padStart(2, "0");
    const day = String(d.getDate()).padStart(2, "0");
    return `${year}-${month}-${day}`;
  };

  const fetchTimesheetData = async (currentPage, currentRowsPerPage, filterMissedShifts = false) => {
    try {
      setLoading(true);

      const apiPage = currentPage + 1;

      const params = {
        startDate: formatDateToYYYYMMDD(dateRange?.startDate),
        endDate: formatDateToYYYYMMDD(dateRange?.endDate),
        page: apiPage,
        limit: currentRowsPerPage,
      };

      if (filterMissedShifts) {
        params.filterMissedShiftsOnly = true;
      }

      const timeSheetResponse = await getEmployeeTimesheet(userId, params);

      if (timeSheetResponse?.success && timeSheetResponse?.data) {
        setTimesheetData(timeSheetResponse.data);

        if (timeSheetResponse.data.pagination) {
          const newTotalCount = timeSheetResponse.data.pagination.totalRecords;
          setTotalCount(newTotalCount);
        }
      } else {
        setTimesheetData({ rows: [], stats: {} });
        setTotalCount(0);
      }
    } catch (error) {
      // console.error("Error fetching timesheet data:", error);
      setTimesheetData({ rows: [], stats: {} });
      setTotalCount(0);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    const fetchAttendance = async () => {
      try {
        const [attendanceResponse, scheduleResponse] = await Promise.all([
          getAttendanceDetails(userId),
          getEmployeeSchedule(userId, checkInRevamped),
        ]);

        if (attendanceResponse?.attendanceDetails) {
          setAttendanceDetails(attendanceResponse?.attendanceDetails);
        }

        if (scheduleResponse?.schedule) {
          setSchedule(scheduleResponse.schedule);
        }

        if (scheduleResponse?.timezone) {
          setTimezone(scheduleResponse.timezone);
        }
      } catch (error) {
        // console.error("Error fetching attendance data:", error);
      }
    };

    fetchAttendance();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    if (dateRange?.startDate && dateRange?.endDate) {
      fetchTimesheetData(page, rowsPerPage, showMissedShiftsOnly);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [dateRange, showMissedShiftsOnly, page, rowsPerPage]);

  const handleRowAction = (action, row) => {
    setSelectedDetailRow(row);
    setDetailModalOpen(true);
  };

  const handleCloseDetailModal = () => {
    setDetailModalOpen(false);
    setSelectedDetailRow(null);
  };

  const handleTimesheetUpdateSuccess = () => {
    // Refresh the timesheet data after successful update
    fetchTimesheetData(page, rowsPerPage, showMissedShiftsOnly);
  };

  const handleOpenEditShiftModal = async () => {
    // Fetch schedule data BEFORE opening modal to populate it
    try {
      const scheduleData = await getEmployeeSchedule(userId, checkInRevamped);
      if (scheduleData) {
        if (scheduleData.schedule) {
          setSchedule(scheduleData.schedule);
        }
        if (scheduleData.timezone) {
          setTimezone(scheduleData.timezone);
        }
      }
      setEditShiftModalOpen(true);
    } catch (error) {
      // console.error("Error fetching schedule data:", error);
      // Still open modal even if fetch fails
      setEditShiftModalOpen(true);
    }
  };

  const handleCloseEditShiftModal = () => {
    setEditShiftModalOpen(false);
  };

  const handleEditShiftSuccess = async () => {
    // Refresh attendance details after successful update
    try {
      const attendanceResponse = await getAttendanceDetails(userId);
      if (attendanceResponse?.attendanceDetails) {
        setAttendanceDetails(attendanceResponse?.attendanceDetails);
      }

      const scheduleData = await getEmployeeSchedule(userId, checkInRevamped);
      if (scheduleData) {
        if (scheduleData.schedule) {
          setSchedule(scheduleData.schedule);
        }
        if (scheduleData.timezone) {
          setTimezone(scheduleData.timezone);
        }
      }
    } catch (error) {
      // console.error("Error fetching attendance data:", error);
    }
  };

  const isToday = (dateString) => {
    const today = new Date();
    const rowDate = new Date(dateString);
    return (
      rowDate.getDate() === today.getDate() &&
      rowDate.getMonth() === today.getMonth() &&
      rowDate.getFullYear() === today.getFullYear()
    );
  };

  const isEdgeAdmin = loggedInUser?.type === "edge";

  const columns = [
    {
      id: "date",
      label: "Date",
    },
    {
      id: "status",
      label: "Status",
      render: (value, row) => {
        if (row.isMissedShift) {
          return (
            <div className="missed-shift-status">
              <CustomTooltip
                content="Please reach out HR directly in case of any concerns and queries"
                direction="bottom-end"
                classes={{
                  tooltip: "custom-tooltip missed-shift-tooltip-content",
                }}
              >
                <span>
                  <CustomChip
                    label="Missed shift"
                    size="medium"
                    variant="filled"
                    color="error"
                    endIcon={<InfoOutlinedIcon className="chip-icon" />}
                  />
                </span>
              </CustomTooltip>
            </div>
          );
        }

        const statusColorMap = {
          "Worked day": "default",
          Holiday: "default",
          "Annual Leave": "default",
          "Half worked day": "default",
          Weekend: "default",
        };

        return (
          <CustomChip
            label={row.status}
            variant="filled"
            color={statusColorMap[row.status] || "default"}
            size="small"
          />
        );
      },
    },
    {
      id: "clockedIn",
      label: "Clocked in",
    },
    {
      id: "clockedOut",
      label: "Clocked out",

      render: (value, row) => {
        if (row.hasNextDay && row.clockedOut !== "-") {
          return (
            <div style={{ display: "flex", alignItems: "center", gap: "8px" }}>
              {row.clockedOut}
              <CustomChip label="Next day" variant="filled" color="default" size="small" className="next-day-chip" />
            </div>
          );
        }
        return row.clockedOut;
      },
    },
    {
      id: "timeWorked",
      label: "Time worked",
    },
    {
      id: "breakDuration",
      label: "Break duration",
    },
    {
      id: "nursing",
      label: "Nursing",
    },
    {
      id: "actions",
      label: "",

      type: "action",
      actions: [
        {
          icon: <ChevronRightIcon />,
          hidden: (row) => {
            // PRD-1471: a worked public holiday keeps its "Holiday" label but should still be
            // editable when it has a clock-in entry. Empty holidays / weekends stay hidden.
            if (row.isHoliday) {
              return !row.timesheetId;
            }
            if (row.isWeekend) {
              return true;
            }
            if (row.isMissedShift) {
              return !isEdgeAdmin;
            }
            return false;
          },
        },
      ],
    },
  ];

  const handleDateRangeChange = (value) => {
    setDateRange(value);
    setPage(0);
    setTableResetKey((prev) => prev + 1);
  };

  const handleShowMissedShifts = () => {
    setShowMissedShiftsOnly(!showMissedShiftsOnly);
    setPage(0);
    setTableResetKey((prev) => prev + 1);
  };

  const handleExportCSV = async () => {
    try {
      const startDate = formatDateToYYYYMMDD(dateRange?.startDate);
      const endDate = formatDateToYYYYMMDD(dateRange?.endDate);

      const params = {
        startDate,
        endDate,
        filterMissedShiftsOnly: showMissedShiftsOnly,
      };

      const blob = await exportEmployeeTimesheetCSV(userId, params);

      const url = window.URL.createObjectURL(blob);
      const link = document.createElement("a");
      link.href = url;
      const filterSuffix = showMissedShiftsOnly ? "_missed_shifts" : "";
      link.download = `timesheet_${userId}_${startDate}_to_${endDate}${filterSuffix}.csv`;
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      window.URL.revokeObjectURL(url);
    } catch (error) {
      // console.error("Error exporting CSV:", error);
    }
  };

  const handlePageChange = (newPage) => {
    setPage(newPage);
  };

  const handleRowsPerPageChange = (newRowsPerPage) => {
    setRowsPerPage(newRowsPerPage);
    setPage(0);
    setTableResetKey((prev) => prev + 1);
  };

  const tableKey = `table-${tableResetKey}`;

  return (
    <>
      {platformHeader && <TopHeader title="Timesheet" backIcon="/main/dashboard" />}

      <div className="timesheet-container">
        <div className="timesheet-controls">
          <DateRangeSelect
            name="dateRange"
            label="Date Range"
            placeholder="Mar 14, 2025 - Apr 14, 2025"
            value={dateRange}
            onChange={handleDateRangeChange}
            dateRangeComponent={
              <Daterange
                months={2}
                leavesCalender={[]}
                holidaysCalender={[]}
                isSwappedLeaves={false}
                initialDateRange={dateRange}
              />
            }
          />

          <div className="timesheet-actions">
            <CustomButton
              label="Show missed shifts"
              variant="outlined"
              color="primary"
              startIcon={showMissedShiftsOnly ? <CheckIcon /> : null}
              className={showMissedShiftsOnly ? "missed-shifts-button-active" : ""}
              onClick={handleShowMissedShifts}
            />
            <CustomButton
              label="Export CSV"
              variant="outlined"
              color="primary"
              startIcon={<FileDownloadOutlinedIcon />}
              onClick={handleExportCSV}
            />
          </div>
        </div>
        {loading ? (
          <PageLoader />
        ) : (
          <>
            <div className="timesheet-summary">
              <div className="summary-card">
                <div className="summary-label-container">
                  <p className="summary-label">Current shift time ({timezone || "N/A"})</p>
                  {checkInRevamped && (
                    <IconButton
                      size="small"
                      onClick={handleOpenEditShiftModal}
                      className="edit-shift-icon"
                      aria-label="Edit shift timings"
                    >
                      <EditOutlinedIcon fontSize="small" />
                    </IconButton>
                  )}
                </div>
                <div className="summary-value">{formatCurrentDaySchedule()}</div>
              </div>
              <div className="summary-card">
                <p className="summary-label">Time worked</p>
                <div className="summary-value">
                  {convertSecondsToHoursMinutes(timesheetData.stats?.totalTimeWorkedSeconds)}
                </div>
              </div>
              <div className="summary-card">
                <p className="summary-label">Time spent on break</p>
                <div className="summary-value">
                  {convertSecondsToHoursMinutes(timesheetData.stats?.totalBreakSeconds)}
                </div>
              </div>
              <div className="summary-card">
                <p className="summary-label">Time spent nursing</p>
                <div className="summary-value">
                  {convertSecondsToHoursMinutes(timesheetData.stats?.totalNursingBreakSeconds)}
                </div>
              </div>
              <div className="summary-card">
                <p className="summary-label">Total missed shifts</p>
                <div className="summary-value">{timesheetData.stats?.totalMissedShifts || 0}</div>
              </div>
            </div>

            <div className="timesheet-table">
              <TableComponent
                key={tableKey}
                columns={columns}
                data={transformedTableData}
                rowsPerPage={rowsPerPage}
                page={page}
                totalCount={totalCount}
                initialRowsPerPage={rowsPerPage}
                onPageChange={handlePageChange}
                onRowsPerPageChange={handleRowsPerPageChange}
                onAction={handleRowAction}
                tableHeadSmall
                tableCellSmall
                rowsPerPageOptions={ROWS_PER_PAGE_OPTIONS}
                hasVerticalBorder
                disableSort
              />
            </div>
          </>
        )}
      </div>

      {checkInRevamped ? (
        <TimesheetDetailModalV2
          open={detailModalOpen}
          onClose={handleCloseDetailModal}
          rowData={selectedDetailRow}
          isEditable={selectedDetailRow ? isToday(selectedDetailRow.date) : true}
          attendanceDetails={attendanceDetails}
          employeeId={userId}
          employeeDetails={employeeDetails}
          onUpdateSuccess={handleTimesheetUpdateSuccess}
        />
      ) : (
        <TimesheetDetailModal
          open={detailModalOpen}
          onClose={handleCloseDetailModal}
          rowData={selectedDetailRow}
          isEditable={selectedDetailRow ? isToday(selectedDetailRow.date) : true}
          attendanceDetails={attendanceDetails}
          employeeId={userId}
          employeeDetails={employeeDetails}
          onUpdateSuccess={handleTimesheetUpdateSuccess}
        />
      )}

      <EditShiftModal
        open={editShiftModalOpen}
        onClose={handleCloseEditShiftModal}
        onSuccess={handleEditShiftSuccess}
        employeeId={userId}
        timezone={timezone}
        currentSchedule={schedule || []}
      />
    </>
  );
}

export default Timesheet;
